import { Button, Card, Collapse, Combobox, Flex, Grid, ScrollArea, SegmentedControl, useCombobox } from '@mantine/core';
import { IconChevronDown, IconChevronRight, IconPlus, IconX } from '@tabler/icons-react';
import { useState } from 'react';

import { resolveAvailableFilters, resolveFilter } from '../../helpers/ruleTemplates';
import AddCustomFieldModal from '../Modals/AddCustomFieldModal';
import RuleFilter from './RuleFilter';
import Rules from './Rules';

const Rule = ({
  icon,
  index,
  rule,
  updateRule,
  removeRule,
  emails,
  campaigns,
  webhooks,
  pushNotifications,
  textMessages,
  viberMessages,
  eventActions,
  eventTags,
  toggle,
  open,
  availableCustomFields,
  readOnly,
  filters,
  pages,
  subscriptions,
  rules,
  updateRules,
  availableRules,
  availableFilters,
  flattenProductCustomFields = false,
  segments,
  whatsappMessages,
  invertible,
  surveys,
}) => {
  const [addCustomFieldModalOpen, setAddCustomFieldModalOpen] = useState();
  const [customFieldScope, setCustomFieldScope] = useState();
  const [filterSearch, setFilterSearch] = useState('');
  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption();
      combobox.focusTarget();
      setFilterSearch('');
    },
    onDropdownOpen: () => {
      combobox.focusSearchInput();
    },
  });

  const isNested = ['or', 'companyScope'].includes(rule.type);
  const ruleAvailableFilters = resolveAvailableFilters(rule, availableRules);

  const addFilter = (filterKey) => {
    const modifiedRule = { ...rule };
    modifiedRule.filters = [...rule.filters];
    modifiedRule.filters.push(Object.assign({}, { values: [] }, filters[filterKey], ruleAvailableFilters[filterKey]));
    updateRule(modifiedRule);
  };

  const removeFilter = (index) => {
    const modifiedRule = { ...rule };
    modifiedRule.filters = [...modifiedRule.filters];
    modifiedRule.filters.splice(index, 1);
    updateRule(modifiedRule);
  };

  const updateFilter = (index, filter) => {
    const modifiedRule = { ...rule, filters: [...rule.filters] };
    modifiedRule.filters[index] = filter;
    updateRule(modifiedRule);
  };

  const addCustomFieldFilter = (params, cb) => {
    let inputType;
    let conditionType;

    if (params.type === 'string') {
      inputType = 'string';
      conditionType = 'term';
    } else if (params.type === 'date') {
      inputType = 'dateRange';
      conditionType = 'absoluteTimeRange';
    } else if (params.type === 'numeric') {
      inputType = 'range';
      conditionType = 'floatRange';
    } else {
      return;
    }

    let field =
      customFieldScope === 'product' && !flattenProductCustomFields
        ? `products.custom.${params.type}.${params.field}`
        : `custom.${params.type}.${params.field}`;

    if (rule.key === 'hasCart') {
      field = `cart.${field}`;
    }

    const modifiedRule = { ...rule };
    modifiedRule.filters = [...rule.filters];
    modifiedRule.filters.push({
      name: `${`${customFieldScope[0].toUpperCase()}${customFieldScope.slice(1)}`} ${params.name}`,
      field,
      inputType,
      conditionType,
      values: [],
    });
    updateRule(modifiedRule);
    cb();
  };

  if (
    rule === undefined ||
    emails === undefined ||
    campaigns === undefined ||
    eventActions === undefined ||
    eventTags === undefined ||
    textMessages === undefined ||
    viberMessages === undefined ||
    pushNotifications === undefined ||
    pages === undefined ||
    subscriptions === undefined ||
    segments === undefined ||
    whatsappMessages === undefined ||
    webhooks === undefined ||
    surveys === undefined
  ) {
    return '';
  }

  const deviceType = ['Desktop', 'Mobile', 'Tablet', 'Android', 'iPhone'];
  const shipmentStatuses = eventActions.filter((action) => action.startsWith('shipment'));
  // Tokenless recommenders are skipped - RuleFilter builds its select data with
  // `o.id.toString()`, so an option with an undefined id crashes the whole rule editor.
  const productBlockOptions = pages.flatMap((p) =>
    (p.recommenders || []).filter((r) => r.token).map((r) => ({ label: `${p.name} > ${r.name}`, id: r.token }))
  );
  const bannerBlockOptions = pages.flatMap((p) =>
    (p.bannerBlocks || []).filter((b) => b.token).map((b) => ({ label: `${p.name} > ${b.name}`, id: b.token }))
  );

  const subscriptionOptions = subscriptions.map((e) => {
    return { label: e, id: e };
  });

  const fieldValueOptions = {
    'products.emailId': emails.map((e) => {
      return { label: e.name, id: e.id };
    }),
    'custom.string.surveyId': surveys.map((e) => {
      return { label: e.name, id: e.id };
    }),
    'products.campaignId': campaigns.map((e) => {
      return { label: e.name, id: e.id };
    }),
    'products.pushNotificationId': pushNotifications.map((e) => {
      return { label: e.name, id: e.id };
    }),
    'products.textMessageId': textMessages.map((e) => {
      return { label: e.name, id: e.id };
    }),
    'products.viberMessageId': viberMessages.map((e) => {
      return { label: e.name, id: e.id };
    }),
    'products.whatsappMessageId': whatsappMessages.map((e) => {
      return { label: e.name, id: e.id };
    }),
    'products.webhookId': webhooks.map((e) => {
      return { label: e.name, id: e.id };
    }),
    action: eventActions.map((e) => {
      return { label: e, id: e };
    }),
    subscriptions: subscriptionOptions,
    // Event rules read the subscription list off the event's custom string entry
    // rather than the profiles column (see behaviorSegmentRules), and this map is
    // keyed by field - without the alias RuleFilter gets no options and crashes.
    'custom.string.subscriptions': subscriptionOptions,
    tags: eventTags.map((e) => {
      return { label: e, id: e };
    }),
    'products.campaignTags': Array.from(
      new Map(campaigns.flatMap((c) => c.tags).map((t) => [t.name, { label: t.name, id: t.name }])).values()
    ),
    emailId: emails.map((e) => {
      return { label: e.name, id: e.id };
    }),
    campaignId: campaigns.map((e) => {
      return { label: e.name, id: e.id };
    }),
    pushNotificationId: pushNotifications.map((e) => {
      return { label: e.name, id: e.id };
    }),
    textMessageId: textMessages.map((e) => {
      return { label: e.name, id: e.id };
    }),
    viberMessageId: viberMessages.map((e) => {
      return { label: e.name, id: e.id };
    }),
    pageToken: pages.map((e) => {
      return { label: e.name, id: e.token };
    }),
    'products.recommenderId': productBlockOptions,
    recommenderId: productBlockOptions,
    'products.bannerBlockId': bannerBlockOptions,
    bannerBlockId: bannerBlockOptions,
    'userAgent->tags': deviceType.map((e) => {
      return { label: e, id: e };
    }),
    segmentId: segments
      .filter((s) => s.scope === 'behavior')
      .map((e) => {
        return { label: e.name, id: e.id };
      }),
    shipmentStatus: shipmentStatuses.map((s) => {
      return { label: s, id: s };
    }),
    whatsappMessageId: whatsappMessages.map((e) => {
      return { label: e.name, id: e.id };
    }),
    webhookId: webhooks.map((e) => {
      return { label: e.name, id: e.id };
    }),
  };

  const filterElements = rule.filters.map((filter, index) => {
    const resolvedFilter = resolveFilter(filter, filters, ruleAvailableFilters);

    return (
      <div key={index} style={{ color: 'var(--mantine-color-dimmed)' }}>
        <RuleFilter
          readOnly={readOnly}
          filter={resolvedFilter}
          filterOptions={fieldValueOptions[resolvedFilter.field]}
          updateFilter={(filter) => updateFilter(index, filter)}
          removeFilter={() => removeFilter(index)}
          ruleInverseSelection={rule.inverseSelection}
        />
      </div>
    );
  });

  // Build available filter options for combobox
  const availableFilterOptions = [];
  for (const filterKey of Object.keys(ruleAvailableFilters)) {
    availableFilterOptions.push({
      key: filterKey,
      label: filters[filterKey].name,
      type: 'filter',
    });
  }

  if (availableCustomFields.product && availableCustomFields.product.length > 0) {
    availableFilterOptions.push({
      key: 'productCustom',
      label: 'Product custom field...',
      type: 'customField',
      scope: 'product',
    });
  }

  const isSurvey = rule.conditions?.some((c) => c.field === 'actionClass' && c.values.includes('survey'));

  const ruleCustomFieldScope = isSurvey ? 'survey' : rule.type;
  const availableCustomFieldsForScope = availableCustomFields[ruleCustomFieldScope];
  if (availableCustomFieldsForScope?.length > 0) {
    availableFilterOptions.push({
      key: 'ruleCustom',
      label: isSurvey
        ? 'Survey field...'
        : `${ruleCustomFieldScope[0].toUpperCase() + ruleCustomFieldScope.slice(1)} custom field...`,
      type: 'customField',
      scope: ruleCustomFieldScope,
    });
  }

  return (
    <Card withBorder>
      <Card.Section inheritPadding py="sm">
        <Flex justify="space-between" align="center" gap="xs" wrap="nowrap">
          <Flex align="center" gap="xs" style={{ minWidth: 0, flex: 1 }}>
            <span style={{ flexShrink: 0 }}>{icon}</span>
            <Button
              aria-controls={`collapse-${index}`}
              aria-expanded={open}
              variant="subtle"
              onClick={toggle}
              style={{ minWidth: 0, flex: 1, justifyContent: 'flex-start' }}
              styles={{ label: { minWidth: 0, display: 'flex', alignItems: 'center' } }}
              rightSection={open ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
            >
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {rule.inverseSelection ? `Not ${rule.name}` : rule.name}
              </span>
            </Button>
          </Flex>
          <Button disabled={readOnly} variant="subtle" onClick={removeRule} style={{ flexShrink: 0 }}>
            <IconX size={14} />
          </Button>
        </Flex>

        <Collapse expanded={open}>
          <div id={`collapse-${index}`}>
            <hr />
            {invertible !== false && (
              <>
                <Grid data-tour="segmentModeSelect" align="center">
                  <Grid.Col span={{ base: 12, sm: 3 }}>Mode</Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 9 }}>
                    <SegmentedControl
                      disabled={readOnly}
                      size="sm"
                      value={String(rule.inverseSelection)}
                      onChange={(value) => updateRule(Object.assign({}, rule, { inverseSelection: value === 'true' }))}
                      data={[
                        { label: rule.name, value: 'false' },
                        { label: `Not ${rule.name}`, value: 'true' },
                      ]}
                    />
                  </Grid.Col>
                </Grid>
                <hr />
              </>
            )}

            {isNested && (
              <Rules
                grouping={rule.type === 'or' ? 'or' : 'and'}
                emails={emails}
                campaigns={campaigns}
                webhooks={webhooks}
                pushNotifications={pushNotifications}
                eventTags={eventTags}
                eventActions={eventActions}
                rules={rule.rules}
                updateRules={(nestedRules) => {
                  const updatedRules = JSON.parse(JSON.stringify(rules));
                  updatedRules[index].rules = nestedRules;
                  updateRules(updatedRules);
                }}
                textMessages={textMessages}
                viberMessages={viberMessages}
                availableCustomFields={availableCustomFields}
                readOnly={readOnly}
                pages={pages}
                subscriptions={subscriptions}
                availableRules={availableRules}
                availableFilters={availableFilters}
                flattenProductCustomFields={flattenProductCustomFields}
                segments={segments}
                whatsappMessages={whatsappMessages}
                surveys={surveys}
              />
            )}
            {!isNested && availableFilterOptions.length > 0 && (
              <Grid>
                <Grid.Col span={12}>
                  <div data-tour="segmentPageFilters">
                    <Combobox
                      store={combobox}
                      width={350}
                      position="bottom-start"
                      withArrow
                      onOptionSubmit={(val) => {
                        const option = availableFilterOptions.find((o) => o.key === val);
                        if (option) {
                          if (option.type === 'filter') {
                            addFilter(option.key);
                          } else if (option.type === 'customField') {
                            setAddCustomFieldModalOpen(true);
                            setCustomFieldScope(option.scope);
                          }
                        }
                        combobox.closeDropdown();
                      }}
                    >
                      <Combobox.Target withAriaAttributes={false}>
                        <Button
                          variant="default"
                          disabled={readOnly}
                          onClick={() => combobox.toggleDropdown()}
                          leftSection={<IconPlus size={16} />}
                        >
                          Add Filters
                        </Button>
                      </Combobox.Target>

                      <Combobox.Dropdown>
                        <Combobox.Search
                          value={filterSearch}
                          onChange={(event) => setFilterSearch(event.currentTarget.value)}
                          placeholder="Search filters..."
                        />
                        <Combobox.Options>
                          <ScrollArea.Autosize type="auto" mah={300}>
                            {availableFilterOptions
                              .filter((option) => option.label.toLowerCase().includes(filterSearch.toLowerCase()))
                              .map((option) => (
                                <Combobox.Option key={option.key} value={option.key}>
                                  {option.label}
                                </Combobox.Option>
                              ))}
                            {availableFilterOptions.filter((option) =>
                              option.label.toLowerCase().includes(filterSearch.toLowerCase())
                            ).length === 0 && <Combobox.Empty>No filters found</Combobox.Empty>}
                          </ScrollArea.Autosize>
                        </Combobox.Options>
                      </Combobox.Dropdown>
                    </Combobox>
                  </div>
                </Grid.Col>
              </Grid>
            )}
            <br />
            <div data-tour="segmentSelectedFilterElements">{filterElements}</div>
          </div>
        </Collapse>
      </Card.Section>
      <AddCustomFieldModal
        key="segmentRule"
        show={addCustomFieldModalOpen}
        save={(params, cb) => addCustomFieldFilter(params, cb)}
        availableCustomFields={customFieldScope ? availableCustomFields[customFieldScope] : []}
        close={() => {
          setAddCustomFieldModalOpen(false);
          setCustomFieldScope(undefined);
        }}
      />
    </Card>
  );
};

export default Rule;
